// src/pages/PropertyManagersEditor.js
// Multi-manager picker used by the Add Property and Edit Property forms.
// Styled by PropertiesClients.css (rules scoped under .pcl-scope, which the page
// root and its portaled popups both carry).

import React from "react";
import { MANAGER_TITLES, readCompanyManagers } from "./managerUtils";

const NEW_VALUE = "__new__";

const emptyRow = (isNew = false) => ({ name: "", title: "", email: "", phone: "", isNew });

const PropertyManagersEditor = ({ company, managers, onChange }) => {
  const rows = managers || [];
  const companyManagers = readCompanyManagers(company).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );
  const hasCompany = Boolean(company);

  const update = (next) => onChange(next);

  const setRow = (index, patch) =>
    update(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const handlePick = (index, value) => {
    if (value === NEW_VALUE) {
      setRow(index, emptyRow(true));
      return;
    }
    const match = companyManagers.find((m) => m.name === value);
    setRow(
      index,
      match ? { ...match, isNew: false } : emptyRow(false)
    );
  };

  const addRow = () => update([...rows, emptyRow(!hasCompany)]);

  const removeRow = (index) => update(rows.filter((_, i) => i !== index));

  const makePrimary = (index) => {
    const next = [...rows];
    const [picked] = next.splice(index, 1);
    update([picked, ...next]);
  };

  // A manager already picked in another row is not offered again.
  const takenNames = (exceptIndex) =>
    new Set(
      rows
        .filter((row, i) => i !== exceptIndex && !row.isNew && row.name)
        .map((row) => row.name.toLowerCase())
    );

  return (
    <div className="pcl-mgr">
      <div className="pcl-mgr-head">
        <span className="pcl-mgr-heading">Managers</span>
        <span className="pcl-mgr-hint">
          The primary manager receives proposal, report and completion emails.
        </span>
      </div>

      {rows.length === 0 && (
        <p className="pcl-mgr-empty">
          {hasCompany
            ? "No managers on this property yet."
            : "Select a management company to pick from its managers, or add one by hand."}
        </p>
      )}

      {rows.map((row, index) => {
        const taken = takenNames(index);
        const options = companyManagers.filter((m) => !taken.has(m.name.toLowerCase()));
        const pickerValue = row.isNew ? NEW_VALUE : row.name;

        return (
          <div className={`pcl-mgr-row${index === 0 ? " is-primary" : ""}`} key={index}>
            <div className="pcl-mgr-row-top">
              {index === 0 ? (
                <span className="pcl-mgr-primary">Primary</span>
              ) : (
                <span className="pcl-mgr-order">Additional</span>
              )}
              <div className="pcl-mgr-row-actions">
                {index > 0 && (
                  <button type="button" className="blue-button pcl-btn-sm" onClick={() => makePrimary(index)}>
                    Make Primary
                  </button>
                )}
                <button type="button" className="red-button pcl-btn-sm" onClick={() => removeRow(index)}>
                  Remove
                </button>
              </div>
            </div>

            {hasCompany && (
              <select value={pickerValue} onChange={(e) => handlePick(index, e.target.value)}>
                <option value="">Select Manager</option>
                {options.map((m) => (
                  <option key={m.name} value={m.name}>
                    {m.title ? `${m.name} (${m.title})` : m.name}
                  </option>
                ))}
                <option value={NEW_VALUE}>+ New manager (adds to {company.management || "company"})</option>
              </select>
            )}

            {row.isNew ? (
              <div className="pcl-mgr-grid">
                <input
                  placeholder="Manager Name"
                  value={row.name}
                  onChange={(e) => setRow(index, { name: e.target.value })}
                />
                <select value={row.title} onChange={(e) => setRow(index, { title: e.target.value })}>
                  <option value="">Select Title</option>
                  {MANAGER_TITLES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <input
                  placeholder="Email"
                  value={row.email}
                  onChange={(e) => setRow(index, { email: e.target.value })}
                />
                <input
                  placeholder="Phone"
                  value={row.phone}
                  onChange={(e) => setRow(index, { phone: e.target.value })}
                />
              </div>
            ) : (
              row.name && (
                <div className="pcl-mgr-grid">
                  <input value={row.title || "Title not set"} readOnly />
                  <input value={row.email} placeholder="Email" readOnly />
                  <input value={row.phone} placeholder="Phone" readOnly />
                </div>
              )
            )}
          </div>
        );
      })}

      <button type="button" className="green-button pcl-btn-sm" onClick={addRow}>
        + Add Manager
      </button>
    </div>
  );
};

export default PropertyManagersEditor;
